<script>
/* Module scope: shared by every ModalBox instance so stacked modals can't
   release the body scroll lock while another one is still open. */
let scrollLockCount = 0;

const acquireScrollLock = () => {
  scrollLockCount += 1;
  document.body.style.overflow = "hidden";
};

const releaseScrollLock = () => {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) document.body.style.overflow = "";
};

/* Open dialogs, innermost last. Every open ModalBox listens on window, so
   without this one Escape closed EVERY stacked dialog at once, and each of them
   fought over Tab. Only the top of the stack answers keys. */
const openStack = [];
let instanceCount = 0;

/* Each open panel's return-focus target, so a dialog opened FROM another
   dialog that then closes (one replacing the other in the same tick) can fall
   back to the first dialog's own target instead of a button that is gone. */
const returnTargets = new WeakMap();

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/* `getClientRects()` rather than `offsetParent`: offsetParent is null for
   position:fixed elements even when they are on screen. */
const isVisible = (el) => !!el && el.getClientRects().length > 0;
</script>

<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import { mdiClose } from "@mdi/js";
import Icon from "@/components/Icon.vue";
import JbButton from "@/components/JbButton.vue";

const props = defineProps({
  title: {
    type: String,
    default: null,
  },
  largeTitle: {
    type: String,
    default: null,
  },
  button: {
    type: String,
    default: "info",
  },
  buttonLabel: {
    type: String,
    default: "Done",
  },
  hasCancel: Boolean,
  modelValue: {
    type: [String, Number, Boolean],
    default: null,
  },
  /* Opt-in: the built-in footer used to be unreachable (the props existed but
     nothing rendered them), so every call site supplies its own buttons in the
     default slot. Defaulting to false keeps all of those rendering exactly as
     they do today, while making the footer available to new callers. */
  showButtons: {
    type: Boolean,
    default: false
  },
  dismissable: {
    type: Boolean,
    default: true,
  },
  /* Set while the dialog holds unsaved input. A stray click on the scrim, the X
     or Escape then asks "Discard your changes?" inside the dialog instead of
     silently throwing the input away. The dialog's own Cancel button is an
     explicit choice and is not intercepted. */
  confirmDiscard: {
    type: Boolean,
    default: false,
  },
  discardMessage: {
    type: String,
    default: "Discard your changes?",
  },
  /* Accessible name for a dialog with neither `title` nor `largeTitle`. */
  ariaLabel: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["update:modelValue", "cancel", "confirm"]);

const value = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

instanceCount += 1;
const dialogId = `modal-box-${instanceCount}`;
const titleId = `${dialogId}-title`;

const panelRef = ref(null);
const keepEditingRef = ref(null);
const discardPrompt = ref(false);

const labelledBy = computed(() => (props.title || props.largeTitle ? titleId : null));

const confirmCancel = (mode) => {
  discardPrompt.value = false;
  value.value = false;
  emit(mode);
};

const cancel = () => confirmCancel("cancel");

const confirm = () => confirmCancel("confirm");

/* Every user-initiated dismissal — scrim, X, Escape — comes through here. */
const requestDismiss = () => {
  if (!props.dismissable) return;
  if (props.confirmDiscard) {
    discardPrompt.value = true;
    nextTick(() => keepEditingRef.value?.focus());
    return;
  }
  cancel();
};

const keepEditing = () => {
  discardPrompt.value = false;
  nextTick(() => panelRef.value?.focus({ preventScroll: true }));
};

const onOverlayClick = () => requestDismiss();

const onCloseClick = () => requestDismiss();

const isTopmost = () => openStack[openStack.length - 1] === dialogId;

const trapTab = (e) => {
  const panel = panelRef.value;
  if (!panel) return;

  const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(isVisible);
  if (!items.length) {
    e.preventDefault();
    panel.focus({ preventScroll: true });
    return;
  }

  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;

  if (!panel.contains(active)) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  } else if (e.shiftKey && (active === first || active === panel)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
};

const onKeydown = (e) => {
  if (!isTopmost()) return;

  if (e.key === "Tab") {
    trapTab(e);
    return;
  }

  if (e.key !== "Escape") return;

  // An open multiselect inside the dialog owns Escape: it closes the list.
  if (e.target?.closest?.(".multiselect.is-open")) return;

  e.stopPropagation();
  e.preventDefault();

  if (discardPrompt.value) {
    keepEditing();
    return;
  }
  requestDismiss();
};

/* Focus moves INTO the dialog on open — to an element marked `data-autofocus`
   (or `autofocus`) if the caller asked for one, otherwise to the panel itself,
   which carries the accessible name. Auto-focusing the first field instead
   would pop the keyboard up on phones and open any multiselect it landed on. */
let returnFocusTo = null;
let fallbackFocusTo = null;

/* Stacking order follows the open order. Every dialog teleports to <body>, so
   at one shared z-index the one mounted LATER paints on top — and App.vue's
   dialogs (session expired, support chat) mount first. A session-expired
   dialog opening over a page dialog was painted underneath it while holding
   the keyboard. The first dialog keeps 9999, so nothing changes for the
   single-dialog case that tooltips (10000) and popovers (9999) are tuned to. */
const zIndex = ref(9999);

const focusInitial = () => {
  const panel = panelRef.value;
  if (!panel || !value.value) return;
  if (panel.contains(document.activeElement)) return;
  const preferred = panel.querySelector("[data-autofocus], [autofocus]");
  (isVisible(preferred) ? preferred : panel).focus({ preventScroll: true });
};

/* Back to whatever opened the dialog — but only if focus is still inside the
   dialog or was dropped on <body>. If the caller moved focus somewhere on
   purpose while closing, leave it there. */
const restoreFocus = () => {
  let target = returnFocusTo;
  if (!target || !target.isConnected || !isVisible(target)) target = fallbackFocusTo;
  returnFocusTo = null;
  fallbackFocusTo = null;
  if (!target || !target.isConnected || typeof target.focus !== "function") return;
  const active = document.activeElement;
  if (!active || active === document.body || panelRef.value?.contains(active)) {
    target.focus({ preventScroll: true });
  }
};

const removeFromStack = () => {
  const index = openStack.lastIndexOf(dialogId);
  if (index !== -1) openStack.splice(index, 1);
};

/* Body scroll lock — the counter lives in module scope (see the block above) so
   this instance only ever holds one lock at a time. */
let isLockedHere = false;

const lockScroll = () => {
  if (isLockedHere) return;
  isLockedHere = true;
  acquireScrollLock();
};

const unlockScroll = () => {
  if (!isLockedHere) return;
  isLockedHere = false;
  releaseScrollLock();
};

// Keys, scroll lock and focus are held only while the modal is open.
watch(
  value,
  (open) => {
    if (open) {
      if (!openStack.includes(dialogId)) openStack.push(dialogId);
      zIndex.value = 9998 + openStack.length;
      const active = document.activeElement;
      returnFocusTo = active && active !== document.body ? active : null;
      const hostPanel = returnFocusTo?.closest?.("[data-modal-box-panel]");
      fallbackFocusTo = hostPanel ? returnTargets.get(hostPanel) ?? null : null;
      discardPrompt.value = false;
      window.addEventListener("keydown", onKeydown, true);
      lockScroll();
      nextTick(() => {
        if (panelRef.value) returnTargets.set(panelRef.value, returnFocusTo);
        focusInitial();
      });
    } else {
      removeFromStack();
      discardPrompt.value = false;
      window.removeEventListener("keydown", onKeydown, true);
      unlockScroll();
      restoreFocus();
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  removeFromStack();
  window.removeEventListener("keydown", onKeydown, true);
  unlockScroll();
  // Most dialogs are mounted with v-if, so they unmount while still "open" and
  // the watcher above never sees `false`. `returnFocusTo` is only set while
  // open, so this is a no-op for a dialog that already closed normally.
  restoreFocus();
});
</script>

<template>
  <teleport to="body">
    <transition name="modal">
      <div
        v-show="value"
        class="fixed inset-0 flex items-center justify-center p-4"
        :style="{ zIndex }"
      >
        <!-- Scrim -->
        <div
          class="modal-scrim absolute inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm dark:bg-black dark:bg-opacity-70"
          aria-hidden="true"
          @click="onOverlayClick"
        />

        <!-- Panel. The dialog role lives here, not on the full-screen wrapper,
             so its box is the dialog's box. tabindex="-1" lets it take focus
             on open without joining the Tab order. -->
        <div
          :id="dialogId"
          ref="panelRef"
          data-modal-box-panel
          role="dialog"
          aria-modal="true"
          :aria-labelledby="labelledBy || undefined"
          :aria-label="labelledBy ? undefined : (ariaLabel || undefined)"
          tabindex="-1"
          class="modal-panel relative w-full md:w-3/5 lg:w-2/5 max-w-2xl flex flex-col max-h-modal
                 bg-white dark:bg-gray-800
                 border border-gray-200 dark:border-gray-700
                 rounded-xl shadow-2xl overflow-hidden focus:outline-none"
        >
          <header
            v-if="title"
            class="flex items-center gap-3 flex-none px-5 py-4 border-b border-gray-200 dark:border-gray-700"
          >
            <h2 :id="titleId" class="flex-grow font-semibold text-gray-900 dark:text-gray-100 truncate" :title="title">
              {{ title }}
            </h2>
            <button
              v-if="dismissable"
              type="button"
              aria-label="Close dialog"
              class="flex-none inline-flex items-center justify-center w-8 h-8
                     min-w-[2.75rem] min-h-[2.75rem] sm:min-w-0 sm:min-h-0 rounded-lg text-gray-400
                     hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
              @click="onCloseClick"
            >
              <icon :path="mdiClose" :size="20" w="w-5" h="h-5" />
            </button>
          </header>

          <!-- Titleless modals get a floating close button instead of a header
               bar. `min-w/min-h` floors it at the 44px touch minimum on phones;
               it is the one control every modal depends on. -->
          <button
            v-else-if="dismissable"
            type="button"
            aria-label="Close dialog"
            class="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-8 h-8
                   min-w-[2.75rem] min-h-[2.75rem] sm:min-w-0 sm:min-h-0 rounded-lg text-gray-400
                   hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
            @click="onCloseClick"
          >
            <icon :path="mdiClose" :size="20" w="w-5" h="h-5" />
          </button>

          <!-- That floating button overlaps the slot content, which only has
               `p-5` to work with — at 360px a wrapping heading ran straight
               under the X. Reserve a gutter for exactly the case that creates
               it. 4rem clears the button's 12px inset plus its 44px box with a
               little air; rendering the header bar unconditionally was the
               alternative, but that would put an empty grey strip on top of
               every titleless dialog in the app. -->
          <div
            class="p-5 overflow-y-auto"
            :class="{ 'pr-16': !title && dismissable }"
          >
            <div class="space-y-3">
              <!-- h2, not h1: the page under the dialog already has its h1. -->
              <h2
                v-if="largeTitle"
                :id="title ? undefined : titleId"
                class="text-xl font-bold text-gray-900 dark:text-gray-100"
              >
                {{ largeTitle }}
              </h2>
              <slot />
            </div>
          </div>

          <!-- Unsaved-changes guard; see `confirmDiscard`. Pinned to the bottom
               of the panel so it cannot scroll out of reach in a long form. -->
          <div
            v-if="discardPrompt"
            class="flex-none flex flex-col gap-3 px-5 py-4 border-t border-amber-200 bg-amber-50
                   sm:flex-row sm:items-center dark:border-amber-800 dark:bg-amber-900/30"
            data-testid="modal-discard-prompt"
          >
            <p role="alert" class="flex-1 text-sm font-medium text-amber-900 dark:text-amber-100">
              {{ discardMessage }}
            </p>
            <div class="flex flex-col-reverse gap-2 sm:flex-row">
              <button
                ref="keepEditingRef"
                type="button"
                class="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-gray-200 bg-white px-4 text-sm font-medium text-gray-800
                       hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500
                       sm:min-h-[36px] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                @click="keepEditing"
              >
                Keep editing
              </button>
              <button
                type="button"
                class="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-red-600 px-4 text-sm font-medium text-white
                       hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2
                       sm:min-h-[36px] dark:focus-visible:ring-offset-gray-800"
                @click="cancel"
              >
                Discard
              </button>
            </div>
          </div>

          <footer
            v-if="showButtons"
            class="flex-none flex items-center justify-end gap-2 px-5 py-4
                   border-t border-gray-200 dark:border-gray-700
                   bg-gray-50 dark:bg-gray-900"
          >
            <jb-button
              v-if="hasCancel"
              color="white"
              label="Cancel"
              @click="cancel"
            />
            <jb-button
              :color="button"
              :label="buttonLabel"
              @click="confirm"
            />
          </footer>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
/* `max-h-modal` (tailwind.config.js) is `calc(100vh - 160px)`. On mobile
   browsers `100vh` is the *large* viewport — it deliberately ignores the
   collapsing URL bar — so a tall dialog could run past the visible area and
   hide its own footer behind the browser chrome. `100dvh` tracks the height
   that is actually on screen. The `100vh` line stays as the fallback for
   engines without dynamic viewport units, same two-declaration pattern as
   `.aside-inline` in css/_translate-rtl.css. Lives here rather than in the
   config because the utility can only carry one value. */
.modal-panel {
  max-height: calc(100vh - 160px);
  max-height: calc(100dvh - 160px);
}

/* A plain fade, in and out. Scrim and panel fade together as one layer and
   the panel keeps its size and place: nothing on screen moves when a dialog
   opens or closes. */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 150ms ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
